import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import LabelWithInput from "@components/input/LabelWithInput";
import { Country } from "@pages/join/_components/Country";
import { Address } from "@pages/join/_components/Address";
import BasicButton from "@components/button/BasicButton";
import { useApiRequest } from "@utils/ApiRequest";
import CustomTextInput from "@components/input/CustomTextInput";
import { zodResolver } from "@hookform/resolvers/zod";
import { profileEditSchema } from "@libs/zod/zodVaildation";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import { Navigate, useNavigate } from "react-router-dom";
import BackHeader from "@components/header/BackHeader";
import { Name } from "@pages/join/_components/Name";

const DeliveryManagement = ({ setStep, DeliveryManagement }: any) => {
  const queryClient = useQueryClient();
  const { updateDelivery, getDeliveryList } = useApiRequest();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const { data: deliveryList } = useQuery({
    queryKey: ["deliveryList"],
    queryFn: () => getDeliveryList(),
  });

  const methods = useForm({
    resolver: zodResolver(profileEditSchema),

    defaultValues: {
      name: deliveryList?.receiver_name || "",
      country: deliveryList?.shipping_country || "",
      postcode: deliveryList?.shipping_postcode || "",
      address: deliveryList?.shipping_address || "",
      address_detail: deliveryList?.shipping_address_detail || "",
      account_number: deliveryList?.receiver_phone,
    },
  });

  const { handleSubmit, watch, setValue } = methods;

  const { mutate: updateDeliveryMutate } = useMutation({
    mutationFn: (data: any) => {
      const deliveryUpdateData = {
        receiver_name: data.name,
        shipping_country: data.country,
        shipping_postcode: data.postcode,
        shipping_address: data.address,
        shipping_address_detail: data.address_detail,
        receiver_phone: data.account_number,
      };

      return updateDelivery(deliveryUpdateData);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["deliveryList"] });

      setIsModalOpen(true);
    },
  });

  useEffect(() => {
    if (deliveryList) {
      setValue("name", deliveryList.receiver_name);
      setValue("country", deliveryList.shipping_country);
      setValue("postcode", deliveryList.shipping_postcode);
      setValue("address", deliveryList.shipping_address);
      setValue("address_detail", deliveryList.shipping_address_detail);
      setValue("account_number", deliveryList.receiver_phone);
    }
  }, [deliveryList, setValue]);

  // watch로 country 값의 변화를 감지
  const country = watch("country");

  // country 값이 변경될 때마다 실행
  useEffect(() => {
    // KR이 아닌 경우 address_detail 필드를 리셋
    if (country !== "KR") {
      setValue("address_detail", "");
    }
  }, [country, setValue]);

  const onSubmit = (data: any) => {
    updateDeliveryMutate(data);
  };

  return (
    <FormProvider {...methods}>
      {DeliveryManagement && <BackHeader title="배송지 관리" />}
      <Container>
        <ContentWrapper>
          <Name />

          <LabelWithInput label="휴대폰번호">
            <CustomTextInput
              name="account_number"
              mode="numeric"
              type="number"
            />
          </LabelWithInput>

          <Country />

          <Address required={true} />
        </ContentWrapper>

        <BasicButton
          type="submit"
          title="등록하기"
          onClick={handleSubmit(onSubmit)}
          disabled={
            watch("name") === "" ||
            watch("country") === "" ||
            watch("postcode") === "" ||
            watch("address") === ""
          }
        />
      </Container>

      <BasicModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        activeEvent={() => {
          setIsModalOpen(false);

          if (DeliveryManagement) {
            navigate(-1);
          } else {
            setStep(0);
          }
        }}
        activeName="확인"
        closeEvent={() => setIsModalOpen(false)}
        notice>
        <ModalContent mainMessage="배송지가 등록되었습니다." />
      </BasicModal>
    </FormProvider>
  );
};

export default DeliveryManagement;

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  // height: "calc(100vh - 5.6rem)",
  gap: "4rem",
  padding: "3rem 2rem 4.2rem",
});

const ContentWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "4rem",
});

// const { mutate: updateProfile } = useMutation({
//   mutationFn: (data: any) => updateDelivery(data),
//   onSuccess: (_, data) => {
//     queryClient.invalidateQueries({ queryKey: ["userInfo"] });
//     setUserInfo({ ...userInfo, ...data });
//     setIsModalOpen(true);
//   },
// });
